
import { ThemeSupa } from "@supabase/auth-ui-shared";

type TextAlign = 'left' | 'center' | 'right';

interface AppearanceStyle {
  theme: any;
  variables: any;
  style: {
    button: Record<string, any>;
    input: Record<string, any>;
    label: Record<string, any>;
    container: Record<string, any>;
    anchor: {
      color: string;
      backgroundColor: string;
      padding: string;
      borderRadius: string;
      fontWeight: string;
      transition: string;
      width: string;
      display: string;
      textAlign: TextAlign;
    };
  };
  className: Record<string, string>;
}

export function getAuthTheme() {
  const customTheme = {
    ...ThemeSupa,
    default: {
      colors: {
        brand: '#2563EB',
        brandAccent: '#2563EB',
        brandButtonText: '#FFFFFF',
        defaultButtonBackground: 'transparent',
        defaultButtonBackgroundHover: '#2563EB',
        inputBackground: '#FFFFFF',
        inputBorder: '#D1D5DB',
        inputBorderHover: '#2563EB',
        inputBorderFocus: '#2563EB',
        inputText: '#000000'
      },
      space: {
        buttonPadding: '0.75rem 1rem',
        inputPadding: '0.75rem 1rem',
      },
      fontSizes: {
        baseButtonSize: '1rem',
        baseInputSize: '1rem',
      },
      fonts: {
        bodyFontFamily: 'inherit',
        buttonFontFamily: 'inherit',
      },
      borderWidths: {
        buttonBorderWidth: '1px',
        inputBorderWidth: '1px'
      },
      radii: {
        borderRadiusButton: '0.375rem',
        buttonBorderRadius: '0.375rem',
        inputBorderRadius: '0.375rem'
      }
    }
  };

  const appearanceStyles: AppearanceStyle = {
    theme: customTheme,
    variables: {
      default: {
        colors: {
          brand: '#2563EB',
          brandAccent: '#2563EB',
          brandButtonText: '#FFFFFF',
          defaultButtonBackground: 'transparent',
          defaultButtonBackgroundHover: '#2563EB',
          inputBackground: '#FFFFFF',
          inputBorder: '#D1D5DB',
          inputBorderHover: '#2563EB',
          inputBorderFocus: '#2563EB',
          inputText: '#000000'
        },
        borderWidths: {
          buttonBorderWidth: '1px',
          inputBorderWidth: '1px'
        },
        radii: {
          borderRadiusButton: '6px',
          buttonBorderRadius: '6px',
          inputBorderRadius: '6px'
        },
        space: {
          spaceSmall: '4px',
          spaceMedium: '8px',
          spaceLarge: '12px',
          labelBottomMargin: '6px',
          buttonPadding: '12px 16px',
          inputPadding: '12px 16px'
        }
      }
    },
    style: {
      button: {
        width: '100%',
        padding: '0.75rem 1rem',
        fontSize: '1rem',
        fontWeight: '500',
        transition: 'all 0.2s',
        border: '1px solid #D1D5DB',
        backgroundColor: '#2563EB',
        color: '#FFFFFF'
      },
      input: {
        fontSize: '1rem',
        padding: '0.75rem',
        backgroundColor: '#FFFFFF',
        borderColor: '#D1D5DB',
        color: '#000000',
        transition: 'all 0.2s'
      },
      label: {
        fontSize: '0.875rem',
        fontWeight: '500',
        color: '#000000',
        marginBottom: '0.5rem'
      },
      container: {
        gap: '0.75rem',
        display: 'flex',
        flexDirection: 'column'
      },
      anchor: {
        color: '#000000',
        backgroundColor: 'transparent',
        padding: '0.5rem',
        borderRadius: '0.375rem',
        fontWeight: '500',
        transition: 'all 0.2s',
        width: '100%',
        display: 'inline-block',
        textAlign: 'center'
      }
    },
    className: {
      anchor: 'hover:text-blue-600 hover:bg-blue-50 transition-colors duration-300',
      button: 'hover:bg-blue-700 transition-all duration-300 text-white'
    }
  };

  // Define localization with the correct structure for Auth UI
  const localizationConfig = {
    variables: {
      sign_up: {
        email_label: 'Email',
        password_label: 'Password (minimum 6 characters)',
        email_input_placeholder: 'Enter your email',
        password_input_placeholder: 'Create a password (6+ characters)',
        button_label: 'Sign Up',
        loading_button_label: 'Creating Account...',
        social_provider_text: 'Sign Up / Sign In with {{provider}}',
        link_text: "Don't have an account? Sign up",
        confirmation_text: "Check your email for the confirmation link"
      },
      sign_in: {
        email_label: 'Email',
        password_label: 'Password',
        email_input_placeholder: 'Enter your email',
        password_input_placeholder: 'Enter your password',
        button_label: 'Sign In',
        loading_button_label: 'Signing In...',
        social_provider_text: 'Sign Up / Sign In with {{provider}}',
        link_text: 'Already have an account? Sign in'
      }
    }
  };

  return {
    appearance: appearanceStyles,
    localization: localizationConfig
  };
}
